import Vue from 'vue';

import { success, fail } from '@helpers/result-helper.js'
import toast from '@/services/toasts/index.js'
import { VuexBuilder } from '@helpers/vuexBuilder';


const AVATAR_THUMBNAIL_WIDTH = 150;

const state = {
	employees: [],
	employees_loaded: false,
	employees_loading: false,
	dirty_employee: false,
	basicInfo_saving: false,
	work_saving: false,
	personal_saving: false,
	emergency_saving: false,
	tags_saving: false,
	hrPolicies_saving: false,
	other_saving: false,
	terminate_saving: false,
	isSettingAdminPrivileges: false,
	operations: {
	}
};

const getters = {
	employees: state => {
		return state.employees;
	},
	employeeDirectory: state => {
		// Excludes employees that are in the system for login-only purposes
		return state.employees.filter(x => !x.hideFromEmployeeDirectory);
	},
	orgChart: (state, getters) => {
		let result = [];

		const employees = getters.employeeDirectory
		.filter(x => x.isActive || getters.leaderIds.includes(x.id))
		.map(employee => {
			let avatarUrl = '';
			if (employee.avatarId !== null) {
				avatarUrl = `${Vue.prototype.$apiBaseUrl}/api/${employee.tenantId}/employees/${employee.id}/avatar/${employee.avatarId}?w=${AVATAR_THUMBNAIL_WIDTH}`;
			} else {
				avatarUrl = `https://yourhrsystemstorage.blob.core.windows.net/public/avatar.png`;
			}

			let tags = [];
			const isLeader = getters.leaderIds.includes(employee.id);
			if (!employee.isActive) {
				tags.push('inactive');
			}
			else if (!employee.positionId) {
				tags.push('missing-pd');
			}
			if (employee.positionTitle.toLowerCase().trim() === 'hr consultant') {
				tags.push('hr-consultant');
			}
			if (isLeader) {
				tags.push('manager');
			} 		
			else {
				tags.push('employee');
			}
			
			let note;
			if (!!employee.positionId) {
				note = employee.positionTitle;
			} else {

				const positionsSorted = employee.positions.sort((a, b) => {
					if (new Date(b.startDateUTC) > new Date(a.startDateUTC))
						return 1;
					else if (new Date(b.startDateUTC) < new Date(a.startDateUTC))
						return -1;

					return a.status - b.status;
				})

				note = employee.positions.length > 0 ? positionsSorted[0].title : "No position description";
			}

			if (!employee.isActive && employee.positions.length > 0) {
				note = `${note} (inactive)`;
			}
			
			let model = {
				id: employee.id,
				pid: employee.reportsTo,
				name: employee.fullName,
				firstName: employee.firstName,
				lastName: employee.lastName,
				positionId: employee.positionId,
				title: employee.positionTitle,
				img: avatarUrl,
				department: employee.department,
				positions: employee.positions,
				note: note,
				tags: tags
			}
			return model;
		});

		result = [...result, ...employees];
		return result;
	},
	employeeList: (state, getters) => {
		return getters.employeeDirectory.map(employee => {
			let model = {
				id: employee.id,
				photoUrl: null,
				name: employee.fullName,
				positions: employee.positions.sort((a, b) => {
					if (new Date(b.startDateUTC) > new Date(a.startDateUTC))
						return 1;
					else if (new Date(b.startDateUTC) < new Date(a.startDateUTC))
						return -1;

					return a.status - b.status;
				}),
				hasLivePosition: !!employee.positionId,
				positionTitle: employee.positionTitle,
				hasLogin: employee.hasLogin,
				department: employee.department !== null ? employee.department.name : 'N/A',
				isActive: employee.isActive,
				tags: employee.tags
			};
			return model;
		});
	},
	leaderIds: (state, getters) => {
		return [...new Set(getters.employeeDirectory
			.filter(x => x.reportsTo !== null && x.isActive)
			.map(x => x.reportsTo))];
	},
	employeesMini: state => {
		return state.employees.map(employee => {

			let avatarUrl = '';
			if (employee.avatarId !== null) {
				avatarUrl = `${Vue.prototype.$apiBaseUrl}/api/${employee.tenantId}/employees/${employee.id}/avatar/${employee.avatarId}?w=${AVATAR_THUMBNAIL_WIDTH}`;
			} else {
				avatarUrl = `https://yourhrsystemstorage.blob.core.windows.net/public/avatar.png`;
			}

			let model = {
				id: employee.id,
				firstName: employee.firstName,
				lastName: employee.lastName,
				fullName: employee.fullName,
				email: employee.email,
				reportsTo: employee.reportsTo,
				department: employee.department,
				underlings: employee.underlings,
				avatarUrl: avatarUrl,
				isActive: employee.isActive,
				tags: employee.tags,
				positionTitle: employee.positionTitle
			};
			return model;
		});
	},
	employeesMiniActiveOnly: (state, getters) => {
		return getters.employeeDirectory.filter(x=> x.isActive).map(employee => {

			let avatarUrl = '';
			if (employee.avatarId !== null) {
				avatarUrl = `${Vue.prototype.$apiBaseUrl}/api/${employee.tenantId}/employees/${employee.id}/avatar/${employee.avatarId}?w=${AVATAR_THUMBNAIL_WIDTH}`;
			} else {
				avatarUrl = `https://yourhrsystemstorage.blob.core.windows.net/public/avatar.png`;
			}

			let model = {
				id: employee.id,
				firstName: employee.firstName,
				lastName: employee.lastName,
				fullName: employee.fullName,
				email: employee.email,
				reportsTo: employee.reportsTo,
				department: employee.department,
				underlings: employee.underlings,
				avatarUrl: avatarUrl,
				tags: employee.tags,
				positionTitle: employee.positionTitle
			};
			return model;
		});
	},
	myTeam: (state, getters, rootState, rootGetters) => {
		return getters.employeesMiniActiveOnly.filter(x=> x.department.id === rootGetters['loggedInEmployee'].department.id);
	},
	myUnderlings: (state, getters, rootState, rootGetters) => {
		const currentEmployee = getters.employeesMiniActiveOnly.find(x => x.id === rootGetters['loggedInEmployee'].id);
		const employeeIds = currentEmployee.underlings
		const underlingMinis = getters.employeesMiniActiveOnly.filter(x => employeeIds.includes(x.id));
		return [...underlingMinis, currentEmployee];
	},
	employeeCount: state => {
		return state.employees.length;
	},
	getEmployeeMini: (state, getters) => (id) => {
        return getters.employeesMini.find(x => x.id === id)
    },
	getEmployeeFirstName: (state, getters) => (id) => {
        const found = getters.employeesMini.find(x => x.id === id);
		return !!found ? found.firstName : null;
    },
	isBasicInfoSaving: state => state.basicInfo_saving,
	isWorkSaving: state => state.work_saving,
	isPersonalSaving: state => state.personal_saving,
	isEmergencySaving: state => state.emergency_saving,
	isTagsSaving: state => state.tags_saving,
	isHrPoliciesSaving: state => state.hrPolicies_saving,
	isOtherSaving: state => state.other_saving,
	isTerminateSaving: state => state.terminate_saving,
	isSettingAdminPrivileges: state => state.isSettingAdminPrivileges
};

const actions = {
	async initialize({commit, state, dispatch}) {
		if (!state.employees_loaded && !state.employees_loading) {
			commit('SET_LOADING', true);
			await dispatch('getEmployees')
			commit('SET_LOADING', false);
		}
	},

	getLoggedInEmployee({ rootState }, includeUnderlings = false) {
		return new Promise((resolve, reject) => {
			Vue.axios
			.get(`/api/${rootState.tenantId}/employees/loggedInEmployee?includeUnderlings=${includeUnderlings}`)
			.then(response => {
				resolve(response.data);
			})
			.catch(err => {
				reject(err);
			});
		});
	},
	getEmployees({ commit, rootState }) {
		console.log('Retrieving Employees...');
		commit('GET_EMPLOYEES_PENDING');
		return Vue.axios
			.get(`/api/${rootState.tenantId}/employeeMini`)
			.then(response => {
				commit('GET_EMPLOYEES_SUCCESS', response.data);
			})
			.catch(error => {
				commit('GET_EMPLOYEES_FAILURE', error);
			});
	},
	getEmployee({ commit, rootState }, id) {
		console.log('Retrieving Employee...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.get(`/api/${rootState.tenantId}/employees/${id}`)
				.then(response => {
					resolve(response.data);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	async addEmployee({ commit, dispatch, rootState }, employee) {
		try {
			const response = await Vue.axios.post(`/api/${rootState.tenantId}/employees`, employee)

			commit('ADD_EMPLOYEE_SUCCESS', response.data);
			dispatch('getEmployees');
			dispatch('tags/getTagList', null, { root: true }); // Update Tag list
			dispatch('hrDocuments/get', null, { root: true }); // Update HR Documents (this is necessary because we may have updated the relevant polices)

			return success(response.data)
		}
		catch (err) {
			const errorData = await err.response.data;
			if (err.response.status === 409) {		
				if (errorData.code === "email_in_use") {
					toast.error("Failed to create employee. This email address is already in use")
				} else {
					toast.error("Failed to create employee")
				}
			}
			return fail(errorData)
		}
	},
	async editEmployee({ commit, dispatch, rootState }, employee) {
		try {
			await Vue.axios.put(`/api/${rootState.tenantId}/employees/${employee.id}`, employee);

			dispatch('getEmployees');
			dispatch('tags/getTagList', null, { root: true }); // Update Tag list
			dispatch('hrDocuments/get', null, { root: true }); // Update HR Documents (this is necessary because we may have updated the relevant polices)

			return success(employee)
		}
		catch (err) {
			const errorData = await err.response.data;
			if (err.response.status === 409) {		
				if (errorData.code === "email_in_use") {
					toast.error("Failed to save employee. This email address is already in use")
				} else {
					toast.error("Failed to save employee")
				}
			}
			return fail(errorData)
		}
	},
	updatePosition({ commit, dispatch, rootState }, { employeeId, position }) {
		console.log('Updating Position...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/employees/${employeeId}/position/${position.id}`, position)
				.then(resp => {
					const updatedEmployee = resp.data;
					dispatch('getEmployees');
					commit('EDIT_POSITION_SUCCESS', updatedEmployee);
					resolve(updatedEmployee);
				})
				.catch(err => {
					console.log('Error updating Position');
					console.log(err);
					reject(err);
				});
		});
	},
	addPosition({ rootState, dispatch }, { employeeId, position }) {
		console.log('Adding Position...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${employeeId}/positions`, position)
				.then(resp => {
					dispatch('getEmployees');
					const updatedEmployee = resp.data;
					resolve(updatedEmployee);
				})
				.catch(err => {
					console.log('Error adding Position');
					console.log(err);
					reject(err);
				});
		});
	},
	deletePosition({ rootState, dispatch }, { employeeId, positionId }) {
		console.log('Deleting Position...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/${rootState.tenantId}/employees/${employeeId}/position/${positionId}`)
				.then(resp => {
					dispatch('getEmployees');
					resolve();
				})
				.catch(err => {
					console.log('Error deleting Position');
					console.log(err);
					reject(err);
				});
		});
	},
	deleteEmployee({ commit, dispatch, rootState }, employee) {
		console.log('Deleting Employee...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/${rootState.tenantId}/employees/${employee.id}`)
				.then(resp => {
					commit('DELETE_EMPLOYEE_SUCCESS', employee);
					dispatch('getEmployees');
					dispatch('tags/getTagList', null, { root: true }); // Update Tag list
					resolve(true);
				})
				.catch(err => {
					console.log('Error deleting Employee');
					console.log(err);
					reject(err);
				});
		});
	},
	createLogin({ commit, rootState }, data) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/users/${rootState.tenantId}`, data)
				.then(resp => {
					commit('CREATE_LOGIN_SUCCESS', resp);
					resolve(resp);
				})
				.catch(err => {
					console.log('Error creating login');
					reject(err);
				});
		});
	},
	updatePassword({ commit, rootState }, data) {
		console.log('Updating password for employee...');

		let postObj = {
			password: data.password,
			employeeId: data.employeeId,
			notifyEmployee: data.notifyEmployee
		};

		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/users/${data.userId}/updatepassword`, postObj)
				.then(resp => {
					commit('UPDATE_PASSWORD_SUCCESS', resp);
					resolve(resp);
				})
				.catch(err => {
					console.log('Error updating password');
					console.log(err);
					reject(err);
				});
		});
	},
	removeLogin({ commit, rootState }, data) {
		console.log('Removing Login...');

		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/users/${rootState.tenantId}/${data.employeeId}/${data.userId}`)
				.then(resp => {
					commit('REMOVE_LOGIN_SUCCESS', resp);
					resolve(resp);
				})
				.catch(err => {
					console.log('Error removing login');
					console.log(err);
					reject(err);
				});
		});
	},
	setDirtyEmployee({ commit }, data) {
		commit('SET_DIRTY_EMPLOYEE', data);
	},
	uploadDocument({ dispatch, rootState }, data) {
		data.document.append('type', 0); // general file

		console.log('Uploading Document...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${data.employeeId}/files`, data.document, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				})
				.then(resp => {
					dispatch('getEmployees');
					resolve(resp.data);
				})
				.catch(err => {
					console.log('Error uploading document...');
					console.log(err);
					reject(err);
				});
		});
	},
	uploadAvatar({ dispatch, rootState }, data) {
		console.log('Uploading Avatar...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${data.employeeId}/avatar`, data.file, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				})
				.then(resp => {
					dispatch('getEmployees');
					resolve(resp);
				})
				.catch(err => {
					console.log('Error uploading avatar...');
					console.log(err);
					reject(err);
				});
		});
	},
	deleteDocument({ dispatch, rootState }, data) {
		console.log('Deleting Document...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/${rootState.tenantId}/employees/${data.employeeId}/files/${data.id}`)
				.then(resp => {
					dispatch('getEmployees');
					resolve(resp);
				})
				.catch(err => {
					console.log('Error deleting document...');
					console.log(err);
					reject(err);
				});
		});
	},
	downloadDocument({ dispatch, rootState }, data) {
		Vue.axios({
			url: `/api/${rootState.tenantId}/employees/${data.employeeId}/Files/${data.id}`,
			method: 'GET',
			responseType: 'blob'
		}).then(response => {
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', data.fileName);
			document.body.appendChild(link);
			link.click();
		});
	},
	getGoal({ rootState }, { employeeId, goalId }) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.get(`/api/${rootState.tenantId}/employees/${employeeId}/goals/${goalId}`)
				.then(response => {
					resolve(response.data);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	addGoalComment({ commit, dispatch, rootState }, { employeeId, goalId, ...comment}) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${employeeId}/goals/${goalId}/comments`, comment)
				.then(resp => {
					resolve(resp.data);
				})
				.catch(err => {
					console.log('Error creating goal comment');
					console.log(err);
					reject(err);
				});
		});
	},
	deleteGoalComment({ rootState }, { employeeId, goalId, id }) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/${rootState.tenantId}/employees/${employeeId}/goals/${goalId}/comments/${id}`)
				.then(resp => {
					resolve(resp);
				})
				.catch(err => {
					console.log('Error deleting comment...');
					console.log(err);
					reject(err);
				});
		});
	},
	createGoal({ commit, dispatch, rootState }, { employeeId, goal }) {
		console.log('Creating Goal...');

		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${employeeId}/goals/`, goal)
				.then(resp => {
					let obj = {
						employeeId: employeeId,
						goal: resp.data
					};
					commit('CREATE_GOAL_SUCCESS', obj);
					resolve(resp.data);
				})
				.catch(err => {
					console.log('Error creating Goal');
					console.log(err);
					reject(err);
				});
		});
	},
	updateGoal({ commit, dispatch, rootState }, { employeeId, goal }) {
		console.log('Updating Goal...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/employees/${employeeId}/goals/${goal.id}`, goal)
				.then(resp => {
					let foundGoal = resp.data.goals.find(x => x.id === goal.id);

					let obj = {
						employeeId: employeeId,
						goal: foundGoal
					};
					commit('EDIT_GOAL_SUCCESS', obj);
					resolve(goal);
				})
				.catch(err => {
					console.log('Error updating Goal');
					console.log(err);
					reject(err);
				});
		});
	},
	deleteGoal({ commit, dispatch, rootState }, { employeeId, goalId }) {
		console.log('Deleting Goal...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/${rootState.tenantId}/employees/${employeeId}/goals/${goalId}`)
				.then(resp => {
					let obj = {
						employeeId: employeeId,
						goalId: goalId
					};

					commit('DELETE_GOAL_SUCCESS', obj);
					resolve(resp.data);
				})
				.catch(err => {
					console.log('Error deleting goal...');
					console.log(err);
					reject(err);
				});
		});
	},
	updateOrgChart({ commit, dispatch, rootState }, data) {
		console.log('Updating Org Chart...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/orgChart`, data)
				.then(resp => {	
					commit('EDIT_ORGCHART_SUCCESS', data);
					return dispatch('getEmployees').then(() => {
						resolve(data);
					})				
				})
				.catch(err => {
					console.log('Error updating Goal');
					console.log(err);
					reject(err);
				});
		});
	},
	updateOrgChartNode({ commit, dispatch, rootState }, data) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/orgChart/updateNode/${data.id}`, data)
				.then(resp => {	
					resolve(data);
					commit('EDIT_ORGCHART_NODE_SUCCESS', data);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	approvePosition({ commit, rootState, dispatch }, { employeeId, positionId }) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${employeeId}/position/${positionId}/approve`)
				.then(resp => {			
					commit('APPROVE_POSITION_SUCCESS');
					dispatch('getEmployees');
					resolve(resp.data);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	acknowledgePosition({ commit, rootState, dispatch }, { employeeId, positionId }) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/${employeeId}/position/${positionId}/acknowledge`)
				.then(resp => {
					commit('ACKNOWLEDGE_POSITION_SUCCESS');
					dispatch('getEmployees');
					resolve(resp.data);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	async isEmailAddressInUse({ rootState }, emailAddress) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/employees/isEmailAddressInUse`, emailAddress)
				.then(resp => {
					resolve(resp.data)
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	async getUserAccount({ rootState }, employeeId) {
		try {
			var response = await Vue.axios.get(`/api/${rootState.tenantId}/employees/${employeeId}/userAccount`);

			if (response.status === 204) {
				return success(null);
			}		

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to load employee's user account details")
			const errorData = await err.response.data;
			return fail(errorData)
		}
	},
	async setMFA({ rootState }, { employeeId, enabled }) {
		try {
			await Vue.axios.post(`/api/${rootState.tenantId}/employees/${employeeId}/setMFA`, enabled,
			{
				headers: {
					'Content-Type': 'application/json',
				},
			});
			return success()
		}
		catch (err) {
			toast.error("Failed to set two-factor authentication")
			const errorData = await err.response.data;
			return fail(errorData)
		}
	},
	async resetMFASettings({ rootState }, employeeId) {
		try {
			await Vue.axios.post(`/api/${rootState.tenantId}/employees/${employeeId}/resetMFASettings`);
			return success()
		}
		catch (err) {
			toast.error("Failed to reset two-factor authentication settings")
			const errorData = await err.response.data;
			return fail(errorData)
		}
	},
	async updateBasicInfo({ commit, rootState }, payload) {
		try {
			commit('BASIC_INFO_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/basicInfo`, payload);

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to update employee details")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('BASIC_INFO_SAVING', false);
		}
	},
	async updateWork({ commit, rootState }, payload) {
		try {
			commit('WORK_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/work`, payload);

			return success(response.data)
		}
		catch (err) {
			const errorData = await err.response.data;
			
			if (err.response.status === 409 && errorData.code === "email_in_use") {	
				toast.error("Failed to update employee. This email address is already in use")
			} else {
				toast.error("Failed to update employee details");
			}
			return fail(errorData)
		}
		finally {
			commit('WORK_SAVING', false);
		}
	},
	async updatePersonal({ commit, rootState }, payload) {
		try {
			commit('PERSONAL_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/personal`, payload);

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to update employee details")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('PERSONAL_SAVING', false);
		}
	},
	async updateEmergency({ commit, rootState }, payload) {
		try {
			commit('EMERGENCY_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/emergency`, payload);

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to update employee details")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('EMERGENCY_SAVING', false);
		}
	},
	async updateTags({ commit, rootState, dispatch }, payload) {
		try {
			commit('TAGS_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/tags`, payload);

			await dispatch('tags/getTagList', null, { root: true }); // Update Tag list

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to update employee tags")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('TAGS_SAVING', false);
		}
	},
	async updateHrPolicies({ commit, dispatch, rootState }, payload) {
		try {
			commit('HR_POLICIES_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/hrPolicies`, payload);
			
			await dispatch('hrDocuments/get', null, { root: true }); // Update HR Documents (this is necessary because we may have updated the relevant polices)

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to update HR Policies")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('HR_POLICIES_SAVING', false);
		}
	},
	async updateOther({ commit, rootState }, payload) {
		try {
			commit('OTHER_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/profile/other`, payload);

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to update employee details")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('OTHER_SAVING', false);
		}
	},
	async terminate({ commit, rootState, dispatch }, payload) {
		try {
			commit('TERMINATE_SAVING', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/terminate`, payload);
			dispatch('getEmployees');
			dispatch('assets/get', null, { root: true });

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed to terminate employee")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('TERMINATE_SAVING', false);
		}
	},
	async setAdminPrivileges({ commit, rootState }, payload) {
		try {
			commit('SETTING_ADMIN_PRIVILEGES', true);

			const response = await Vue.axios.put(`/api/${rootState.tenantId}/employees/${payload.employeeId}/setAdminPrivileges`, payload);

			return success(response.data)
		}
		catch (err) {
			toast.error("Failed setting admin privileges")
			const errorData = await err.response.data;
			return fail(errorData)
		}
		finally {
			commit('SETTING_ADMIN_PRIVILEGES', false);
		}
	},
	async getImportEmployeeTemplate({ commit, rootState }) {
		await new VuexBuilder(commit)
			.operationKey('downloading')
			.request(() => Vue.axios({
				url: `/api/${rootState.tenantId}/employees/import/csv`,
				method: 'GET',
				responseType: 'blob'
			}))
			.errorToast("Failed to download file(s)")
			.download("employee-import-template.csv")
	},
	async uploadEmployeeImportCsv({commit, rootState}, {csvFile, isCreate}) {

		const request = new FormData()

		request.set("create", isCreate)
		request.set("csvFile", csvFile)

		const successMsg = isCreate ? 'Successfully imported' : 'Successfully validated'

		return await new VuexBuilder(commit)
			.operationKey('uploading')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/employees/import/csv`, request))
			.errorToast('There was errors using this csv.')
			.successToast(successMsg)
			.execute();
	}
};

const mutations = {
	SET_LOADING(state, loading) {
		state.employees_loading = loading;
	},
	START_OPERATION(state, operationKey) {
		state.operations[operationKey] = true
	},
	FINISH_OPERATION(state, operationKey) {
		state.operations[operationKey] = false
	},
	GET_EMPLOYEES_PENDING(state) {
		state.employees_loading = true;
	},
	GET_EMPLOYEES_SUCCESS(state, data) {
		state.employees_loading = false;
		state.employees = data;
	},
	GET_EMPLOYEES_FAILURE(state, error) {
		state.employees_loading = false;
		console.log(error);
	},
	GET_ORGCHART_PENDING(state) {
		state.orgChart_loading = true;
	},
	GET_ORGCHART_SUCCESS(state, data) {
		state.orgChart_loading = false;
		state.orgChart = data;
	},
	GET_ORGCHART_FAILURE(state, error) {
		state.orgChart_loading = false;
		console.log(error);
	},
	ADD_EMPLOYEE_SUCCESS(state, data) {
		// state.employees.push(data)
	},
	DELETE_EMPLOYEE_SUCCESS(state, data) {
		const index = state.employees.indexOf(data);
		state.employees.splice(index, 1);
	},
	SET_DIRTY_EMPLOYEE(state, data) {
		state.dirty_employee = data;
	},
	EDIT_ORGCHART_NODE_SUCCESS(state, data) {
		const index = state.employees.findIndex(x => x.id === data.id);
		if (index > -1) {
			const updatedEmployee = {
				...state.employees[index],
				firstName: data.firstName,
				lastName: data.lastName,
				fullName: `${data.firstName} ${data.lastName}`,
				positionTitle: data.title
			};
			state.employees.splice(index, 1, updatedEmployee)
		}
	},
	BASIC_INFO_SAVING(state, val) {
		state.basicInfo_saving = val;
	},
	WORK_SAVING(state, val) {
		state.work_saving = val;
	},
	PERSONAL_SAVING(state, val) {
		state.personal_saving = val;
	},
	EMERGENCY_SAVING(state, val) {
		state.emergency_saving = val;
	},
	TAGS_SAVING(state, val) {
		state.tags_saving = val;
	},
	HR_POLICIES_SAVING(state, val) {
		state.hrPolicies_saving = val;
	},
	OTHER_SAVING(state, val) {
		state.other_saving = val;
	},
	TERMINATE_SAVING(state, val) {
		state.terminate_saving = val;
	},
	SETTING_ADMIN_PRIVILEGES(state, val) {
		state.isSettingAdminPrivileges = val;
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
